import React, { useState } from "react";
import { Mail, Phone, MapPin, Linkedin } from "lucide-react";
import { motion, easeOut } from "framer-motion";
import Tilt from "react-parallax-tilt";

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: easeOut },
  },
};

const media = [
  { type: "image", src: "/header-1.png" },
  { type: "video", src: "/header-2.mp4" },
  { type: "video", src: "/header-3.mp4" }
];

const BusinessCard = () => {
  const [current, setCurrent] = useState(0);
  const nextPhoto = () => setCurrent((prev) => (prev + 1) % media.length);
  const prevPhoto = () => setCurrent((prev) => (prev - 1 + media.length) % media.length);

  // Function to download vCard as fallback
  const downloadVCard = () => {
    const vCardData = `BEGIN:VCARD\nVERSION:3.0\nFN:Jai Sharma, MSc\nTITLE:Senior VP, Synapse MedCom\nEMAIL:jai@synapsemedcom.ca\nTEL:+1-647-668-4281\nEND:VCARD`;
    const blob = new Blob([vCardData], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-synapse-light via-synapse to-synapse-dark px-4">
      <motion.div
        className="w-full max-w-md"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.6 }}
        variants={cardVariants}
      >
        <Tilt
          tiltMaxAngleX={10}
          tiltMaxAngleY={10}
          perspective={1000}
          scale={1.02}
          transitionSpeed={1000}
          className="rounded-3xl shadow-2xl"
        >
          <div className="bg-synapse-light/20 backdrop-blur-xl border border-synapse-light/30 rounded-3xl p-8 w-full shadow-2xl">
            {/* Slideshow */}
            <div className="mb-6">
              <div className="relative w-full h-40 rounded-xl overflow-hidden flex items-center justify-center bg-synapse-light/30">
                {media[current].type === "image" ? (
                  <motion.img
                    key={current}
                    src={media[current].src}
                    alt={`Slideshow ${current + 1}`}
                    className="object-cover w-full h-full"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.5 }}
                  />
                ) : (
                  <motion.video
                    key={current}
                    src={media[current].src}
                    className="object-contain w-full h-full bg-black"
                    autoPlay
                    loop
                    muted
                    controls={false}
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.5 }}
                  />
                )}
                <button
                  onClick={prevPhoto}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white text-gray-700 rounded-full p-1 shadow transition"
                  aria-label="Previous photo"
                >
                  &#8592;
                </button>
                <button
                  onClick={nextPhoto}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white text-gray-700 rounded-full p-1 shadow transition"
                  aria-label="Next photo"
                >
                  &#8594;
                </button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                  {media.map((_, idx) => (
                    <span
                      key={idx}
                      className={`w-2 h-2 rounded-full ${idx === current ? 'bg-synapse' : 'bg-gray-300'}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center text-center space-y-5">
              {/* Company Logo */}
              <div className="flex justify-center mb-4">
                <img
                  src="/synapse-logo.png"
                  alt="Company Logo"
                  className="max-w-[160px] w-full h-auto object-contain"
                  style={{ background: 'transparent' }}
                />
              </div>

              {/* Profile Image */}
              <motion.div
                className="relative"
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <img
                  src="/jai-sharma.jpeg"
                  alt="Profile"
                  className="w-28 h-28 rounded-full object-cover object-center border-4 border-white shadow-md scale-110"
                />
                <div className="absolute inset-0 rounded-full ring-2 ring-synapse animate-pulse"></div>
              </motion.div>

              {/* Name and Title */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                viewport={{ once: true }}
              >
                <h1 className="text-2xl font-bold text-gray-800">Jai Sharma, MSc</h1>
                <p className="text-sm text-gray-600">Senior VP, Medical Communications</p>
              </motion.div>

              {/* Contact Info */}
              <motion.div
                className="w-full pt-4 border-t border-gray-200"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="space-y-3 mt-4 text-base text-gray-700">
                  <ContactItem icon={Mail} label="jai@synapsemedcom.ca" link="mailto:jai@synapsemedcom.ca" />
                  <ContactItem icon={Phone} label="647-668-4281" link="tel:+16476684281" />
                  <ContactItem icon={MapPin} label="synapsemedcom.ca" link="https://synapsemedcom.ca" />
                  <ContactItem
                    icon={Linkedin}
                    label="linkedin.com/in/jai-sharma"
                    link="https://www.linkedin.com/in/jai-sharma-03526a6/"
                  />
                </div>
              </motion.div>
            </div>
            <div className="mt-8 flex justify-center">
              <button
                onClick={() => {
                  if ('contacts' in navigator && 'ContactsManager' in window) {
                    const contact = {
                      name: ['Jai Sharma, MSc'],
                      email: ['jai@synapsemedcom.ca'],
                      tel: ['+1-647-668-4281'],
                      title: ['Senior VP, Medical Communications'],
                      org: ['Synapse MedCom']
                    };
                    
                    (navigator as any).contacts.select(['name', 'email', 'tel'], { multiple: false })
                      .then((contacts: any) => {
                        console.log('Contact selected:', contacts);
                      })
                      .catch((err: any) => {
                        console.error('Error selecting contact:', err);
                        downloadVCard();
                      });
                  } else {
                    downloadVCard();
                  }
                }}
                className="bg-synapse-dark hover:bg-synapse text-white font-semibold py-2 px-6 rounded-full shadow transition"
              >
                Add me to contacts
              </button>
            </div>

            {/* QR Code - only shown on the desktop version */}
            <div className="mt-6 flex flex-col items-center hidden sm:flex">
                              <div className="w-40 h-40 bg-white/50 rounded-2xl border-2 border-synapse-light/40 flex items-center justify-center shadow-lg overflow-hidden">
                {/* QR Code placeholder - you can add an actual QR code image here that takes users to this business card site*/}
                <img src="/qrcode-synapse-resrc-ca-jaibc-md.png" alt="QR Code" className="w-full h-full object-contain" />
              </div>
              <p className="mt-3 text-sm text-gray-700 font-medium">To view this on your phone, scan this QR code</p>
            </div>
          </div>
        </Tilt>
      </motion.div>
    </div>
  );
};

const ContactItem = ({
  icon: Icon,
  label,
  link,
}: {
  icon: any;
  label: string;
  link?: string;
}) => (
  <motion.div
    className="flex items-center gap-3 group"
    whileHover={{ scale: 1.03 }}
    transition={{ type: "spring", stiffness: 200 }}
  >
    <Icon className="w-5 h-5 text-gray-800 group-hover:text-gray-900 transition" />
    {link ? (
      <a href={link} target="_blank" rel="noopener noreferrer" className="hover:underline">
        {label}
      </a>
    ) : (
      <span>{label}</span>
    )}
  </motion.div>
);

export default BusinessCard;
